import { FontService, FontVariantStyle } from '../../types';
import type { Pattern } from '@web-stories-wp/patterns';
import type {
  FontData,
  TextElement,
  TextElementFont,
  TextFontReferenceIdentity,
  TextFontReferencesV1,
  TextFontReferenceV1,
} from '../../types';
import { generateFontFamily } from './generateFontFamily';
import {
  decodeGradientPattern,
  getGradientPatternStyleValue,
} from '../../../utils/textGradient';
import { parseTextContentBody } from './parseTextContentBody';

export const FONT_REFERENCE_ATTRIBUTE = 'data-st-font-ref';
export const FONT_SIZE_ATTRIBUTE = 'data-st-font-size';
export const LINE_HEIGHT_ATTRIBUTE = 'data-st-line-height';
export const LIVE_TYPOGRAPHY_SCALE_PROPERTY = '--st-live-typography-scale';
export const TEXT_FONT_REFERENCES_VERSION = 1;

const MAX_REFERENCE_ITEMS = 128;
const MAX_REFERENCE_ID_LENGTH = 128;
const MAX_IDENTITY_LENGTH = 512;
const MAX_REVISION_LENGTH = 128;
const MAX_RENDER_FONT_SIZE = 10_000;
const MAX_INLINE_LINE_HEIGHT = 10;
const MAX_FONT_URL_LENGTH = 4096;
const REFERENCE_ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;
// v0015 migrated these names as system fonts. The current trusted catalog
// serves them as hosted custom fonts, so paste revalidation may bridge only
// these known legacy service mismatches by unique family.
const LEGACY_SYSTEM_FONT_FAMILIES = new Set([
  'Arial',
  'Arial Black',
  'Arial Narrow',
  'Baskerville',
  'Brush Script MT',
  'Copperplate',
  'Courier New',
  'Century Gothic',
  'Garamond',
  'Georgia',
  'Gill Sans',
  'Lucida Bright',
  'Lucida Sans Typewriter',
  'Palatino',
  'Papyrus',
  'Tahoma',
  'Times New Roman',
  'Trebuchet MS',
  'Verdana',
]);
const maximumTypographySizeCache = new WeakMap<
  object,
  { content: string; fontSize: number; maximumSize: number }
>();

export function hasMixedTypographyMarkers(content: string) {
  return /<[^>]*\bdata-st-(?:font-(?:ref|size)|line-height)\s*=/i.test(content);
}

function hasMarkerAttribute(content: string, attribute: string) {
  return new RegExp(`<[^>]*\\b${attribute}\\s*=`, 'i').test(content);
}

type TextElementWithTypography = Pick<
  TextElement,
  | 'content'
  | 'font'
  | 'fontSize'
  | 'lineHeight'
  | 'fontReferences'
  | 'fontStyle'
  | 'fontWeight'
>;

export type ResolvedTextRun = {
  content: string;
  font: TextElementFont;
  renderFamily: string;
  fontReferenceId?: string;
  identityKey: string;
  revision?: string;
  fontSize: number;
  fontSizeOverride: boolean;
  lineHeight: number;
  lineHeightOverride: boolean;
  fontWeight: number;
  fontStyle: string;
  letterSpacing: string;
  color?: string;
  gradient?: Pattern;
  gradientKey?: string;
  textDecoration?: string;
  textTransform?: string;
  isLineBreak?: boolean;
  isEmptyLine?: boolean;
};

export type TextFontUsage = Pick<
  ResolvedTextRun,
  | 'font'
  | 'renderFamily'
  | 'fontReferenceId'
  | 'identityKey'
  | 'revision'
  | 'fontWeight'
  | 'fontStyle'
  | 'content'
>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isBoundedString(
  value: unknown,
  maximumLength: number
): value is string {
  return (
    typeof value === 'string' &&
    value.trim().length > 0 &&
    value.length <= maximumLength
  );
}

function canEncodeURIComponent(value: string) {
  try {
    encodeURIComponent(value);
    return true;
  } catch {
    return false;
  }
}

function isFontWeight(value: unknown) {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= 100 &&
    value <= 900 &&
    value % 100 === 0
  );
}

function isFontVariant(value: unknown) {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    [FontVariantStyle.Normal, FontVariantStyle.Italic].includes(value[0]) &&
    isFontWeight(value[1])
  );
}

function isSafeFontUrl(value: unknown): value is string {
  if (
    typeof value !== 'string' ||
    value.length === 0 ||
    value.length > MAX_FONT_URL_LENGTH
  ) {
    return false;
  }
  try {
    const url = new URL(value);
    return ['https:', 'http:'].includes(url.protocol);
  } catch {
    return false;
  }
}

function isFont(value: unknown): value is TextElementFont {
  if (!isRecord(value)) {
    return false;
  }

  if (
    !isBoundedString(value.family, MAX_IDENTITY_LENGTH) ||
    !Object.values(FontService).includes(value.service as FontService)
  ) {
    return false;
  }
  if (
    value.fallbacks !== undefined &&
    (!Array.isArray(value.fallbacks) ||
      !value.fallbacks.every((fallback) =>
        isBoundedString(fallback, MAX_IDENTITY_LENGTH)
      ))
  ) {
    return false;
  }
  if (
    value.variants !== undefined &&
    (!Array.isArray(value.variants) || !value.variants.every(isFontVariant))
  ) {
    return false;
  }
  if (value.service !== FontService.Custom) {
    return true;
  }
  if (!isSafeFontUrl(value.url)) {
    return false;
  }
  return (
    value.urls === undefined ||
    (Array.isArray(value.urls) &&
      value.urls.every(
        (item) =>
          isRecord(item) &&
          isFontVariant(item.variant) &&
          isSafeFontUrl(item.url)
      ))
  );
}

function normalizeIdentity(value: unknown): TextFontReferenceIdentity | null {
  if (!isRecord(value) || !isBoundedString(value.kind, 16)) {
    return null;
  }

  if (
    value.kind === 'asset' &&
    isBoundedString(value.sourceId, MAX_IDENTITY_LENGTH) &&
    canEncodeURIComponent(value.sourceId)
  ) {
    return { kind: 'asset', sourceId: value.sourceId.trim() };
  }

  if (
    value.kind === 'metadata' &&
    isBoundedString(value.fingerprint, MAX_IDENTITY_LENGTH) &&
    canEncodeURIComponent(value.fingerprint)
  ) {
    return { kind: 'metadata', fingerprint: value.fingerprint.trim() };
  }

  return null;
}

function normalizeReference(value: unknown): TextFontReferenceV1 | null {
  if (!isRecord(value)) {
    return null;
  }

  const id = typeof value.id === 'string' ? value.id : '';
  const identity = normalizeIdentity(value.identity);
  const rawRevision = typeof value.revision === 'string' ? value.revision : '';

  if (
    id.length > MAX_REFERENCE_ID_LENGTH ||
    !REFERENCE_ID_PATTERN.test(id) ||
    !identity ||
    !isBoundedString(rawRevision, MAX_REVISION_LENGTH) ||
    !canEncodeURIComponent(rawRevision) ||
    !isFont(value.font)
  ) {
    return null;
  }

  return {
    id,
    identity,
    revision: rawRevision.trim(),
    font: value.font,
  };
}

function getReferencesValue(value: unknown): unknown {
  if (isRecord(value) && 'fontReferences' in value) {
    return value.fontReferences;
  }
  return value;
}

/** Returns true when metadata belongs to a newer reader contract. */
export function hasUnsupportedTextFontReferences(value: unknown): boolean {
  const references = getReferencesValue(value);
  return (
    isRecord(references) &&
    Number.isInteger(references.version) &&
    Number(references.version) > TEXT_FONT_REFERENCES_VERSION
  );
}

/** Tolerantly validates V1 metadata. Duplicate IDs are rejected as ambiguous. */
export function normalizeTextFontReferences(
  value: unknown
): TextFontReferencesV1 | undefined {
  const references = getReferencesValue(value);
  if (
    !isRecord(references) ||
    references.version !== TEXT_FONT_REFERENCES_VERSION ||
    !Array.isArray(references.items) ||
    references.items.length > MAX_REFERENCE_ITEMS
  ) {
    return undefined;
  }

  const counts = new Map<string, number>();
  for (const item of references.items) {
    if (
      isRecord(item) &&
      typeof item.id === 'string' &&
      REFERENCE_ID_PATTERN.test(item.id)
    ) {
      counts.set(item.id, (counts.get(item.id) || 0) + 1);
    }
  }
  const candidates = references.items
    .map(normalizeReference)
    .filter((item): item is TextFontReferenceV1 => Boolean(item));
  const uniqueIdCandidates = candidates.filter(
    (item) => counts.get(item.id) === 1
  );
  const signaturesByIdentity = new Map<string, Set<string>>();
  for (const item of uniqueIdCandidates) {
    const key = getTextFontIdentityKey(item);
    const signatures = signaturesByIdentity.get(key) || new Set();
    signatures.add(`${item.revision}|${stableStringify(item.font)}`);
    signaturesByIdentity.set(key, signatures);
  }
  const items = uniqueIdCandidates.filter(
    (item) => signaturesByIdentity.get(getTextFontIdentityKey(item))?.size === 1
  );
  return { version: TEXT_FONT_REFERENCES_VERSION, items };
}

export function getTextFontReferenceMap(value: unknown) {
  const normalized = normalizeTextFontReferences(value);
  return new Map(normalized?.items.map((item) => [item.id, item]) || []);
}

export function getTextFontIdentityKey(reference: TextFontReferenceV1) {
  return reference.identity.kind === 'asset'
    ? `asset:${reference.identity.sourceId}`
    : `metadata:${reference.identity.fingerprint}`;
}

export function getTextFontRenderFamily(reference: TextFontReferenceV1) {
  if (reference.font.service !== FontService.Custom) {
    return reference.font.family;
  }

  const identityValue =
    reference.identity.kind === 'asset'
      ? reference.identity.sourceId
      : reference.identity.fingerprint;
  return `StorytellerMixed-${reference.identity.kind}-${encodeURIComponent(
    identityValue
  )}-${encodeURIComponent(reference.revision)}`;
}

export function resolveTextFontWeight(value: string, inheritedWeight: number) {
  const fallback = Number.isFinite(inheritedWeight) ? inheritedWeight : 400;
  if (value === 'normal') return 400;
  if (value === 'bold') return 700;
  if (value === 'bolder') {
    if (fallback <= 300) return 400;
    if (fallback <= 500) return 700;
    return 900;
  }
  if (value === 'lighter') {
    if (fallback <= 500) return 100;
    if (fallback <= 700) return 400;
    return 700;
  }

  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= 1 && numeric <= 1000
    ? numeric
    : fallback;
}

export function applyTextTransform(content: string, textTransform?: string) {
  if (textTransform === 'uppercase') return content.toUpperCase();
  if (textTransform === 'lowercase') return content.toLowerCase();
  if (textTransform === 'capitalize') {
    return content.replace(
      /(^|[^\p{L}\p{N}])(\p{L})/gu,
      (_match, prefix, letter) => `${prefix}${letter.toUpperCase()}`
    );
  }
  return content;
}

function stableStringify(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  if (isRecord(value)) {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value) || 'null';
}

function hashString(value: string) {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index++) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

function getMetadataIdentity(font: TextElementFont): TextFontReferenceIdentity {
  const candidateSourceId = (font as FontData & { sourceId?: unknown })
    .sourceId;
  if (
    isBoundedString(candidateSourceId, MAX_IDENTITY_LENGTH) &&
    canEncodeURIComponent(candidateSourceId)
  ) {
    return { kind: 'asset', sourceId: candidateSourceId.trim() };
  }

  const identityInput = {
    service: font.service,
    family: font.family,
    ...(font.service === FontService.Custom
      ? { url: font.url, urls: font.urls }
      : {}),
  };
  return {
    kind: 'metadata',
    fingerprint: `font-${hashString(stableStringify(identityInput))}`,
  };
}

export function createTextFontReference(
  font: TextElementFont,
  currentReferences?: unknown
): TextFontReferenceV1 {
  const identity = getMetadataIdentity(font);
  const identityValue =
    identity.kind === 'asset' ? identity.sourceId : identity.fingerprint;
  const revision = `rev-${hashString(stableStringify(font))}`;
  const baseId = `font-${hashString(`${identity.kind}:${identityValue}`)}`;
  const referenceMap = getTextFontReferenceMap(currentReferences);
  let id = baseId;
  let suffix = 2;

  while (referenceMap.has(id)) {
    const existing = referenceMap.get(id)!;
    if (
      getTextFontIdentityKey(existing) ===
        `${identity.kind}:${identityValue}` &&
      existing.revision === revision
    ) {
      return existing;
    }
    id = `${baseId}_${suffix}`;
    suffix++;
  }

  return { id, identity, revision, font };
}

/**
 * Identifies the same custom font across tenants without trusting its uploaded
 * asset location. The destination catalog remains authoritative for URLs and
 * all persisted metadata.
 */
function getPortableCustomFontSignature(font: TextElementFont) {
  if (font.service !== FontService.Custom) {
    return null;
  }

  return stableStringify({
    family: font.family,
    service: font.service,
    fallbacks: font.fallbacks,
    weights: font.weights,
    styles: font.styles,
    variants: font.variants,
    metrics: font.metrics,
    fsSelection: font.fsSelection,
    useTypoMetrics: font.useTypoMetrics,
  });
}

function findPortableCustomFont(
  incomingFont: TextElementFont,
  trustedFonts: FontData[]
) {
  const incomingSignature = getPortableCustomFontSignature(incomingFont);
  if (!incomingSignature) {
    return undefined;
  }

  const candidates = trustedFonts.filter(
    (font): font is TextElementFont =>
      isFont(font) && getPortableCustomFontSignature(font) === incomingSignature
  );
  return candidates.length === 1 ? candidates[0] : undefined;
}

export function ensureTextFontReference(
  font: TextElementFont,
  currentReferences?: unknown
) {
  const normalized = normalizeTextFontReferences(currentReferences);
  const created = createTextFontReference(font, normalized);
  const identityKey = getTextFontIdentityKey(created);
  const existingIdentity = normalized?.items.find(
    (item) => getTextFontIdentityKey(item) === identityKey
  );
  const reference =
    existingIdentity?.revision === created.revision
      ? existingIdentity
      : existingIdentity
      ? { ...created, id: existingIdentity.id }
      : created;
  const items = existingIdentity
    ? normalized!.items.map((item) =>
        getTextFontIdentityKey(item) === identityKey
          ? item.id === reference.id
            ? reference
            : { ...created, id: item.id }
          : item
      )
    : [...(normalized?.items || []), reference].slice(0, MAX_REFERENCE_ITEMS);

  return {
    reference,
    fontReferences: { version: 1 as const, items },
  };
}

function findLegacySystemCatalogFont(
  incomingFont: TextElementFont,
  trustedFonts: FontData[]
) {
  if (
    incomingFont.service !== FontService.System ||
    !LEGACY_SYSTEM_FONT_FAMILIES.has(incomingFont.family)
  ) {
    return undefined;
  }
  const candidates = trustedFonts.filter(
    (font): font is TextElementFont =>
      isFont(font) && font.family === incomingFont.family
  );
  return candidates.length === 1 ? candidates[0] : undefined;
}

/**
 * Revalidates untrusted reference metadata against the current font catalog.
 * Incoming URLs and metrics are never retained; an identity must resolve to
 * exactly one authoritative catalog snapshot.
 */
export function revalidateTextFontReferences(
  value: unknown,
  trustedFonts: FontData[]
): TextFontReferencesV1 | undefined {
  const normalized = normalizeTextFontReferences(value);
  if (!normalized || !Array.isArray(trustedFonts)) {
    return undefined;
  }

  const fontsByIdentity = new Map<string, Map<string, FontData>>();
  for (const font of trustedFonts) {
    if (!isFont(font)) {
      continue;
    }
    const authoritativeReference = createTextFontReference(font);
    const identityKey = getTextFontIdentityKey(authoritativeReference);
    const snapshots = fontsByIdentity.get(identityKey) || new Map();
    snapshots.set(stableStringify(font), font);
    fontsByIdentity.set(identityKey, snapshots);
  }

  const items = normalized.items.flatMap((reference) => {
    const candidates = Array.from(
      fontsByIdentity.get(getTextFontIdentityKey(reference))?.values() || []
    );
    let authoritativeFont;
    if (candidates.length === 1) {
      authoritativeFont = candidates[0];
    } else if (candidates.length === 0) {
      authoritativeFont =
        findLegacySystemCatalogFont(reference.font, trustedFonts) ||
        findPortableCustomFont(reference.font, trustedFonts);
    }
    if (!authoritativeFont) {
      return [];
    }
    const authoritativeReference = createTextFontReference(authoritativeFont);
    return [
      {
        id: reference.id,
        identity: authoritativeReference.identity,
        revision: authoritativeReference.revision,
        font: authoritativeFont,
      },
    ];
  });

  return items.length ? { version: 1, items } : undefined;
}

/** Resolves one untrusted box-default font to the authoritative catalog. */
export function revalidateTextFont(
  value: unknown,
  trustedFonts: FontData[]
): TextElementFont | undefined {
  if (!isFont(value) || !Array.isArray(trustedFonts)) {
    return undefined;
  }
  const incomingIdentity = getTextFontIdentityKey(
    createTextFontReference(value)
  );
  const candidates = trustedFonts.filter(
    (font) =>
      isFont(font) &&
      getTextFontIdentityKey(createTextFontReference(font)) === incomingIdentity
  );
  if (candidates.length === 1) {
    return candidates[0];
  }
  return candidates.length === 0
    ? findLegacySystemCatalogFont(value, trustedFonts) ||
        findPortableCustomFont(value, trustedFonts)
    : undefined;
}

/** Refreshes matching fonts without rewriting unresolved pasted font intent. */
export function revalidatePastedTextElementFonts(
  element: TextElement,
  trustedFonts: FontData[]
): TextElement {
  const sourceReferences = normalizeTextFontReferences(element.fontReferences);
  const revalidatedReferences = revalidateTextFontReferences(
    sourceReferences,
    trustedFonts
  );
  const hasCompleteReferenceMatch =
    sourceReferences &&
    revalidatedReferences?.items.length === sourceReferences.items.length;

  return {
    ...element,
    font: revalidateTextFont(element.font, trustedFonts) || element.font,
    fontReferences: hasCompleteReferenceMatch
      ? revalidatedReferences
      : element.fontReferences,
  };
}

export function parseInlineFontSize(value: unknown): number | null {
  if (
    (typeof value !== 'string' && typeof value !== 'number') ||
    (typeof value === 'string' && value.trim() === '')
  ) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 && parsed <= MAX_RENDER_FONT_SIZE
    ? parsed
    : null;
}

export function parseInlineLineHeight(value: unknown): number | null {
  if (
    (typeof value !== 'string' && typeof value !== 'number') ||
    (typeof value === 'string' && value.trim() === '')
  ) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) &&
    parsed >= 0 &&
    parsed <= MAX_INLINE_LINE_HEIGHT
    ? parsed
    : null;
}

function canonicalizeNumber(value: number) {
  return Number(value.toFixed(6)).toString();
}

export function scaleInlineFontSizes(content: string, factor: number) {
  if (
    !hasMarkerAttribute(content || '', FONT_SIZE_ATTRIBUTE) ||
    !Number.isFinite(factor) ||
    factor <= 0 ||
    factor === 1
  ) {
    return content;
  }

  const attributePattern = new RegExp(
    `(\\b${FONT_SIZE_ATTRIBUTE}\\s*=\\s*)(["'])([^"']*)(\\2)`,
    'gi'
  );
  const maximumInlineSize = getInlineFontSizes(content).reduce(
    (maximum, value) => Math.max(maximum, value),
    0
  );
  const boundedFactor = maximumInlineSize
    ? Math.min(factor, MAX_RENDER_FONT_SIZE / maximumInlineSize)
    : factor;

  return content.replace(/<[^>]*>/g, (tag) =>
    tag.replace(
      attributePattern,
      (match, prefix, quote, rawValue, closingQuote) => {
        const value = parseInlineFontSize(rawValue);
        return value === null
          ? match
          : `${prefix}${quote}${canonicalizeNumber(
              value * boundedFactor
            )}${closingQuote}`;
      }
    )
  );
}

function getInlineFontSizes(content: string) {
  const sizes: number[] = [];
  const attributePattern = new RegExp(
    `\\b${FONT_SIZE_ATTRIBUTE}\\s*=\\s*["']([^"']*)["']`,
    'gi'
  );
  for (const tag of content.match(/<[^>]*>/g) || []) {
    let match = attributePattern.exec(tag);
    while (match) {
      const value = parseInlineFontSize(match[1]);
      if (value !== null) {
        sizes.push(value);
      }
      match = attributePattern.exec(tag);
    }
    attributePattern.lastIndex = 0;
  }
  return sizes;
}

/** Returns one safe scale for the box default and all inline size overrides. */
export function getBoundedTypographyScale(
  element: Pick<TextElement, 'content' | 'fontSize'>,
  requestedFactor: number
) {
  if (!Number.isFinite(requestedFactor) || requestedFactor <= 0) {
    return 1;
  }
  const cached = maximumTypographySizeCache.get(element);
  let maximumSize = cached?.maximumSize;
  if (
    !cached ||
    cached.content !== element.content ||
    cached.fontSize !== element.fontSize
  ) {
    maximumSize = Math.max(
      parseInlineFontSize(element.fontSize) || 0,
      ...getInlineFontSizes(element.content)
    );
    maximumTypographySizeCache.set(element, {
      content: element.content,
      fontSize: element.fontSize,
      maximumSize,
    });
  }
  return maximumSize
    ? Math.min(requestedFactor, MAX_RENDER_FONT_SIZE / maximumSize)
    : requestedFactor;
}

export function getLiveTypographyFontSizeCss(
  value: number,
  dataToEditorY: (fontSize: number) => number
) {
  return `calc(${dataToEditorY(
    value
  )}px * var(${LIVE_TYPOGRAPHY_SCALE_PROPERTY}, 1))`;
}

export function setLiveTypographyScale(
  target: HTMLElement | null,
  scale: number
) {
  if (!target) {
    return;
  }
  if (Number.isFinite(scale) && scale > 0 && scale !== 1) {
    target.style.setProperty(LIVE_TYPOGRAPHY_SCALE_PROPERTY, String(scale));
  } else {
    target.style.removeProperty(LIVE_TYPOGRAPHY_SCALE_PROPERTY);
  }
}

function getUsedReferenceIds(content: string) {
  const ids = new Set<string>();
  if (!hasMarkerAttribute(content || '', FONT_REFERENCE_ATTRIBUTE)) {
    return ids;
  }
  const attributePattern = new RegExp(
    `\\b${FONT_REFERENCE_ATTRIBUTE}\\s*=\\s*["']([^"']*)["']`,
    'gi'
  );
  for (const tag of content.match(/<[^>]*>/g) || []) {
    let match = attributePattern.exec(tag);
    while (match) {
      const id = match[1];
      if (REFERENCE_ID_PATTERN.test(id)) {
        ids.add(id);
      }
      match = attributePattern.exec(tag);
    }
    attributePattern.lastIndex = 0;
  }
  return ids;
}

export function hasUnresolvedTextFontReferences(
  content: string,
  value: unknown
) {
  const knownIds = new Set(
    normalizeTextFontReferences(value)?.items.map(({ id }) => id) || []
  );
  return Array.from(getUsedReferenceIds(content)).some(
    (id) => !knownIds.has(id)
  );
}

export function pruneUnusedFontReferences(
  content: string,
  value: unknown
): TextFontReferencesV1 | undefined {
  const normalized = normalizeTextFontReferences(value);
  if (!normalized) {
    return undefined;
  }
  const usedIds = getUsedReferenceIds(content);
  const items = normalized.items.filter((item) => usedIds.has(item.id));
  return items.length ? { version: 1, items } : undefined;
}

function getDefaultRunState(
  element: TextElementWithTypography
): Omit<ResolvedTextRun, 'content' | 'isLineBreak'> {
  return {
    font: element.font,
    renderFamily: element.font.family,
    identityKey: `default:${hashString(stableStringify(element.font))}`,
    fontSize: element.fontSize,
    fontSizeOverride: false,
    lineHeight: element.lineHeight,
    lineHeightOverride: false,
    fontWeight: element.fontWeight || 400,
    fontStyle: element.fontStyle || 'normal',
    letterSpacing: '0em',
  };
}

function sameRun(a: ResolvedTextRun, b: ResolvedTextRun) {
  return (
    !a.isLineBreak &&
    !b.isLineBreak &&
    a.font === b.font &&
    a.renderFamily === b.renderFamily &&
    a.fontReferenceId === b.fontReferenceId &&
    a.revision === b.revision &&
    a.fontSize === b.fontSize &&
    a.fontSizeOverride === b.fontSizeOverride &&
    a.lineHeight === b.lineHeight &&
    a.lineHeightOverride === b.lineHeightOverride &&
    a.fontWeight === b.fontWeight &&
    a.fontStyle === b.fontStyle &&
    a.letterSpacing === b.letterSpacing &&
    a.color === b.color &&
    a.gradientKey === b.gradientKey &&
    a.textDecoration === b.textDecoration &&
    a.textTransform === b.textTransform
  );
}

function appendRun(runs: ResolvedTextRun[], run: ResolvedTextRun) {
  if (!run.content && !run.isLineBreak && !run.isEmptyLine) {
    return;
  }
  const previous = runs[runs.length - 1];
  if (
    previous &&
    !previous.isEmptyLine &&
    !run.isEmptyLine &&
    sameRun(previous, run)
  ) {
    previous.content += run.content;
    return;
  }
  runs.push(run);
}

function mergeElementState(
  element: TextElementWithTypography,
  node: HTMLElement,
  inherited: Omit<ResolvedTextRun, 'content' | 'isLineBreak'>,
  referenceMap: Map<string, TextFontReferenceV1>
) {
  const state = { ...inherited };
  const isSpan = node.tagName === 'SPAN';

  if (isSpan && node.hasAttribute?.(FONT_REFERENCE_ATTRIBUTE)) {
    const referenceId = node.getAttribute(FONT_REFERENCE_ATTRIBUTE) || '';
    const reference = referenceId ? referenceMap.get(referenceId) : undefined;
    if (reference) {
      state.font = reference.font;
      state.renderFamily = getTextFontRenderFamily(reference);
      state.fontReferenceId = reference.id;
      state.identityKey = getTextFontIdentityKey(reference);
      state.revision = reference.revision;
    } else {
      const defaultState = getDefaultRunState(element);
      state.font = defaultState.font;
      state.renderFamily = defaultState.renderFamily;
      state.identityKey = defaultState.identityKey;
      delete state.fontReferenceId;
      delete state.revision;
    }
  }

  if (isSpan && node.hasAttribute?.(FONT_SIZE_ATTRIBUTE)) {
    const inlineSize = parseInlineFontSize(
      node.getAttribute(FONT_SIZE_ATTRIBUTE)
    );
    state.fontSize = inlineSize || element.fontSize;
    state.fontSizeOverride = inlineSize !== null;
  }

  if (isSpan && node.hasAttribute?.(LINE_HEIGHT_ATTRIBUTE)) {
    const inlineLineHeight = parseInlineLineHeight(
      node.getAttribute(LINE_HEIGHT_ATTRIBUTE)
    );
    state.lineHeight = inlineLineHeight ?? element.lineHeight;
    state.lineHeightOverride = inlineLineHeight !== null;
  }

  const style = node.style;
  if (style?.fontWeight) {
    state.fontWeight = resolveTextFontWeight(
      style.fontWeight,
      state.fontWeight
    );
  }
  if (style?.fontStyle) {
    state.fontStyle = style.fontStyle;
  } else if (['EM', 'I'].includes(node.tagName)) {
    state.fontStyle = 'italic';
  }
  if (style?.letterSpacing) {
    state.letterSpacing = style.letterSpacing;
  }
  const gradientKey = getGradientPatternStyleValue(style);
  const gradient = decodeGradientPattern(gradientKey);
  if (gradient) {
    state.gradient = gradient;
    state.gradientKey = gradientKey;
  } else if (style?.color) {
    state.color = style.color;
    delete state.gradient;
    delete state.gradientKey;
  }
  const decoration = style?.textDecoration || style?.textDecorationLine;
  if (decoration) {
    state.textDecoration = decoration;
  } else if (node.tagName === 'U') {
    state.textDecoration = 'underline';
  }
  if (style?.textTransform) {
    state.textTransform = style.textTransform;
  }
  if (['B', 'STRONG'].includes(node.tagName) && !style?.fontWeight) {
    state.fontWeight = 700;
  }

  return state;
}

/** Resolves trusted attributes and inherited inline styles into ordered runs. */
export function resolveTextRuns(
  element: TextElementWithTypography
): ResolvedTextRun[] {
  const defaultState = getDefaultRunState(element);
  if (typeof DOMParser === 'undefined') {
    return element.content
      ? [{ ...defaultState, content: element.content }]
      : [];
  }

  const { body } = new DOMParser().parseFromString(
    element.content || '',
    'text/html'
  );
  const referenceMap = getTextFontReferenceMap(element);
  const runs: ResolvedTextRun[] = [];

  const visit = (
    node: Node,
    inherited: Omit<ResolvedTextRun, 'content' | 'isLineBreak'>
  ) => {
    if (node.nodeType === Node.TEXT_NODE) {
      appendRun(runs, {
        ...inherited,
        content: applyTextTransform(
          node.textContent || '',
          inherited.textTransform
        ),
      });
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }
    const htmlNode = node as HTMLElement;
    if (htmlNode.tagName === 'BR') {
      appendRun(runs, { ...inherited, content: '\n', isLineBreak: true });
      return;
    }
    const state = mergeElementState(element, htmlNode, inherited, referenceMap);
    if (
      htmlNode.tagName === 'SPAN' &&
      htmlNode.childNodes.length === 0 &&
      htmlNode.hasAttribute(LINE_HEIGHT_ATTRIBUTE) &&
      state.lineHeightOverride
    ) {
      appendRun(runs, { ...state, content: '', isEmptyLine: true });
      return;
    }
    for (const child of Array.from(htmlNode.childNodes)) {
      visit(child, state);
    }
  };

  for (const child of Array.from(body.childNodes)) {
    visit(child, defaultState);
  }
  return runs.length ? runs : [{ ...defaultState, content: '' }];
}

/** True when every persisted character derives its size from an inline run. */
export function hasOnlyExplicitFontSizeRuns(
  element: TextElementWithTypography
) {
  const contributingRuns = resolveTextRuns(element).filter(
    (run) => !run.isLineBreak && run.content.length > 0
  );
  return (
    contributingRuns.length > 0 &&
    contributingRuns.every((run) => run.fontSizeOverride)
  );
}

/** True when at least one persisted character has an inline size override. */
export function hasInlineFontSizeRuns(element: TextElementWithTypography) {
  return resolveTextRuns(element).some(
    (run) => !run.isLineBreak && run.content.length > 0 && run.fontSizeOverride
  );
}

/**
 * True when an inline size changes visible geometry from the box default.
 * Redundant markers equal to the default should keep the legacy layout path.
 */
export function hasEffectiveInlineFontSizeRuns(
  element: TextElementWithTypography
) {
  return resolveTextRuns(element).some(
    (run) =>
      !run.isLineBreak &&
      run.content.length > 0 &&
      run.fontSizeOverride &&
      run.fontSize !== element.fontSize
  );
}

/** True when at least one persisted character has an inline line-height override. */
export function hasInlineLineHeightRuns(element: TextElementWithTypography) {
  return resolveTextRuns(element).some(
    (run) =>
      !run.isLineBreak &&
      (run.content.length > 0 || run.isEmptyLine) &&
      run.lineHeightOverride
  );
}

/** True when an inline line height changes geometry from the box default. */
export function hasEffectiveInlineLineHeightRuns(
  element: TextElementWithTypography
) {
  return resolveTextRuns(element).some(
    (run) =>
      !run.isLineBreak &&
      (run.content.length > 0 || run.isEmptyLine) &&
      run.lineHeightOverride &&
      run.lineHeight !== element.lineHeight
  );
}

export function collectTextFontUsage(
  element: TextElementWithTypography
): TextFontUsage[] {
  const grouped = new Map<string, TextFontUsage>();
  for (const run of resolveTextRuns(element)) {
    const key = [
      run.identityKey,
      run.revision || '',
      run.renderFamily,
      run.fontWeight,
      run.fontStyle,
    ].join('|');
    const existing = grouped.get(key);
    if (existing) {
      existing.content += run.content;
      continue;
    }
    grouped.set(key, {
      font: run.font,
      renderFamily: run.renderFamily,
      fontReferenceId: run.fontReferenceId,
      identityKey: run.identityKey,
      revision: run.revision,
      fontWeight: run.fontWeight,
      fontStyle: run.fontStyle,
      content: run.content,
    });
  }
  return Array.from(grouped.values());
}

/** Materializes Story-unit attributes into ephemeral renderer CSS. */
export function materializeTextContent(
  element: TextElementWithTypography,
  {
    fontSizeToCss = (value) => `${value}px`,
  }: { fontSizeToCss?: (value: number) => string | number } = {}
) {
  if (!hasMixedTypographyMarkers(element.content || '')) {
    return element.content;
  }
  if (typeof DOMParser === 'undefined') {
    return element.content;
  }

  const body = parseTextContentBody(element.content);
  const referenceMap = getTextFontReferenceMap(element);
  body.querySelectorAll<HTMLElement>('*').forEach((node) => {
    node.style.removeProperty('font-family');
    node.style.removeProperty('font-size');
    node.style.removeProperty('line-height');
    if (node.tagName !== 'SPAN') {
      return;
    }
    const span = node;
    if (span.hasAttribute(FONT_REFERENCE_ATTRIBUTE)) {
      const id = span.getAttribute(FONT_REFERENCE_ATTRIBUTE) || '';
      const reference = id ? referenceMap.get(id) : undefined;
      span.style.fontFamily = reference
        ? generateFontFamily(reference.font, getTextFontRenderFamily(reference))
        : generateFontFamily(element.font);
    }
    if (span.hasAttribute(FONT_SIZE_ATTRIBUTE)) {
      const size = parseInlineFontSize(span.getAttribute(FONT_SIZE_ATTRIBUTE));
      span.style.fontSize = String(fontSizeToCss(size ?? element.fontSize));
    }
    if (span.hasAttribute(LINE_HEIGHT_ATTRIBUTE)) {
      const lineHeight = parseInlineLineHeight(
        span.getAttribute(LINE_HEIGHT_ATTRIBUTE)
      );
      span.style.lineHeight = String(lineHeight ?? element.lineHeight);
      if (span.childNodes.length === 0 && lineHeight !== null) {
        span.style.display = 'inline-block';
        span.style.fontSize = String(fontSizeToCss(element.fontSize));
        span.style.height = `${lineHeight}em`;
        span.style.verticalAlign = 'top';
      }
    }
  });
  return body.innerHTML;
}
